import { Component, OnDestroy, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DealService, CargoDealDetail } from '../../../services/api/deal';
import { AuthService } from '../../../services/auth/auth';
import { RequisitesService, CompanyRequisites } from '../../../services/api/requisites';
import { LanguageService } from '../../../services/i18n/language';
import { DocNumberPipe } from '../../../services/request-number';
import { isDeliveredByCodes, visibleActivities } from '../../../services/deal-stage';
import { ResizableColumnsDirective } from '../../../services/resizable-columns.directive';
import { paymentSummary, CurrencyTotals } from '../../../services/deal-payments';
import { HomeCurrencyService, fmtMoney, sumIn, othersText, totalsText, mergeTotals } from '../../../services/home-currency';
import { lockPageScroll, unlockPageScroll } from '../../../services/scroll-lock';

/** Этапы, после которых груз считается переданным (как в счётчике на странице сделки). */
const HANDOVER_CODES = ['CARGO_PICKUP_DONE', 'CARGO_AT_FORWARDER_WAREHOUSE', 'IN_TRANSIT'];

interface CpDeal {
  id: number;
  createdAt: string;
  route: string;
  stage: string;
  delivered: boolean;
  handedOver: boolean;
  price: number;
  currency: string;
  /** Счета и оплаты по валютам. */
  totals: CurrencyTotals[];
}

interface Counterparty {
  key: string;
  userId: number | null;
  companyName: string;
  fullName?: string;
  phone?: string;
  email?: string;
  deals: CpDeal[];
  lastDealAt: string;
}

/**
 * «Контрагенты» — с кем уже были сделки: у экспедитора это клиенты, у грузовладельца — экспедиторы.
 * В таблице — сводка по каждому, по клику — окно с контактами, реквизитами и списком сделок.
 * Отдельного списка на сервере нет — собираем из карточек сделок.
 */
@Component({
  selector: 'app-ff-counterparties',
  standalone: true,
  imports: [CommonModule, RouterModule, DocNumberPipe, ResizableColumnsDirective],
  templateUrl: './ff-counterparties.component.html',
  styleUrl: './ff-counterparties.component.scss'
})
export class FfCounterpartiesComponent implements OnInit, OnDestroy {
  loading = true;
  error = false;
  counterparties: Counterparty[] = [];
  query = '';

  /** Открытый контрагент (окно с подробностями). */
  selected: Counterparty | null = null;
  requisites: CompanyRequisites | null = null;
  requisitesLoading = false;
  requisitesError = false;

  /** Смотрит грузовладелец: контрагенты — экспедиторы, сделки — его. */
  readonly isOwner: boolean;

  private subscriptions = new Subscription();

  // ─── Деньги — в валюте страны компании; счета в других валютах не пересчитываются ───

  /** Валюта сводок — по стране компании (₸ для Казахстана, € для Европы…). */
  currency = 'KZT';

  /** Сумма в валюте компании. */
  home(v: number): string {
    return fmtMoney(v, this.currency, this.languageService.getCurrentLanguageValue());
  }

  /** Итог по набору сделок: в валюте компании, а если в ней ничего нет — в других валютах. */
  homeSum(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    const v = sumIn(rows, this.currency, field);
    return v > 0 ? this.home(v) : this.dealTotals(mergeTotals(rows), field);
  }

  /** Суммы в других валютах — «+ 3 000 USD»; пусто, если их нет или итог уже показан в них. */
  homeOthers(rows: { totals: CurrencyTotals[] }[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    if (!(sumIn(rows, this.currency, field) > 0)) return '';
    return othersText(rows, this.currency, field, this.languageService.getCurrentLanguageValue());
  }

  /** Поле по сделке (или нескольким) во всех валютах, своя первой; пусто — «0» в своей. */
  dealTotals(totals: CurrencyTotals[], field: 'invoiced' | 'paid' | 'debt' | 'overpaid'): string {
    return totalsText(totals, this.currency, field, this.languageService.getCurrentLanguageValue()) || this.home(0);
  }

  constructor(
    authService: AuthService,
    private dealService: DealService,
    private requisitesService: RequisitesService,
    private homeCurrencyService: HomeCurrencyService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {
    this.isOwner = authService.getUser()?.role?.code === 'ROLE_CARGO_OWNER';
  }

  /** Адрес сделки в кабинете этой роли. */
  get dealBase(): string {
    return this.isOwner ? '/cargo-owner/deal' : '/freight-forwarder/deal';
  }

  /** Текст для экспедитора или для грузовладельца. */
  rt(ffKey: string, ffFallback: string, ownerKey: string, ownerFallback: string): string {
    return this.isOwner ? this.t(ownerKey, ownerFallback) : this.t(ffKey, ffFallback);
  }

  ngOnInit(): void {
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.subscriptions.add(this.homeCurrencyService.currency$.subscribe(c => {
      this.currency = c;
      this.cdr.markForCheck();
    }));
    this.subscriptions.add(
      (this.isOwner ? this.dealService.getMyDealsPaginated(0, 100) : this.dealService.getMyDealsForwarderPaginated(0, 100)).subscribe({
        next: page => {
          const list = page.content ?? [];
          if (!list.length) {
            this.loading = false;
            this.cdr.markForCheck();
            return;
          }
          this.subscriptions.add(
            forkJoin(list.map(d => this.dealService.getDealDetail(d.id).pipe(catchError(() => of(null))))).subscribe(details => {
              this.counterparties = this.group(details.filter((d): d is CargoDealDetail => !!d));
              this.loading = false;
              this.cdr.markForCheck();
            })
          );
        },
        error: () => {
          this.error = true;
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    if (this.selected) this.unlockScroll();
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  private toDeal(d: CargoDealDetail): CpDeal {
    const lang = this.languageService.getCurrentLanguageValue();
    const statuses = visibleActivities(d.activities, this.isOwner ? 'ROLE_CARGO_OWNER' : 'ROLE_FREIGHT_FORWARDER').filter(a => a.activityType?.category === 'STATUS');
    const last = [...statuses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const codes = statuses.map(a => a.activityType?.code);
    return {
      id: d.id,
      createdAt: d.createdAt,
      route: `${d.cargoRequest?.fromCity ?? ''} → ${d.cargoRequest?.toCity ?? ''}`,
      stage: last ? ((lang === 'en' ? last.activityType.nameEn : last.activityType.nameRu) || '—') : '—',
      delivered: isDeliveredByCodes(codes),
      handedOver: codes.some(c => HANDOVER_CODES.includes(c)),
      price: d.bid?.price || 0,
      currency: d.bid?.currencyCode || '',
      totals: paymentSummary(d.activities).totals,
    };
  }

  private group(details: CargoDealDetail[]): Counterparty[] {
    const map = new Map<string, Counterparty>();
    for (const d of details) {
      const p = this.isOwner ? d.freightForwarder : d.cargoOwner;
      const key = String(p?.id ?? p?.companyName ?? d.id);
      let c = map.get(key);
      if (!c) {
        c = { key, userId: p?.id ?? null, companyName: p?.companyName || '—', fullName: p?.fullName, phone: p?.phone, email: p?.email, deals: [], lastDealAt: d.createdAt };
        map.set(key, c);
      }
      c.deals.push(this.toDeal(d));
      if (new Date(d.createdAt) > new Date(c.lastDealAt)) c.lastDealAt = d.createdAt;
    }
    const list = [...map.values()];
    list.forEach(c => c.deals.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    return list.sort((a, b) => new Date(b.lastDealAt).getTime() - new Date(a.lastDealAt).getTime());
  }

  get filtered(): Counterparty[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.counterparties;
    return this.counterparties.filter(c =>
      [c.companyName, c.fullName, c.phone, c.email].some(v => (v || '').toLowerCase().includes(q)));
  }

  // ─── Сводка по контрагенту ───

  delivered(c: Counterparty): number {
    return c.deals.filter(d => d.delivered).length;
  }

  inTransit(c: Counterparty): number {
    return c.deals.filter(d => d.handedOver && !d.delivered).length;
  }

  /** Стоимость перевозок с контрагентом — по выставленным счетам: в валюте компании, другие валюты через « · ». */
  totalText(c: Counterparty): string {
    return this.dealTotals(mergeTotals(c.deals), 'invoiced');
  }

  /** Есть ли долг по счетам в какой-либо валюте. */
  hasDebt(c: Counterparty): boolean {
    return c.deals.some(d => d.totals.some(t => t.debt > 0));
  }

  hasOver(c: Counterparty): boolean {
    return c.deals.some(d => d.totals.some(t => t.overpaid > 0));
  }

  debtText(c: Counterparty): string {
    return this.dealTotals(mergeTotals(c.deals), 'debt');
  }

  overpaidText(c: Counterparty): string {
    return this.dealTotals(mergeTotals(c.deals), 'overpaid');
  }

  money(amount: number, code: string): string {
    return `${this.number(amount)} ${code || ''}`.trim();
  }

  private number(n: number): string {
    const lang = this.languageService.getCurrentLanguageValue();
    const locale = ({ ru: 'ru-RU', kk: 'kk-KZ', en: 'en-US', zh: 'zh-CN' } as Record<string, string>)[lang] ?? 'ru-RU';
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n || 0);
  }

  dealsLabel(n: number): string {
    if (this.languageService.getCurrentLanguageValue() !== 'ru') return n + ' ' + this.t('ffCp.dealsShort', 'сделок');
    const m10 = n % 10, m100 = n % 100;
    const word = m10 === 1 && m100 !== 11 ? 'сделка' : m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20) ? 'сделки' : 'сделок';
    return n + ' ' + word;
  }

  initials(name: string): string {
    return name.replace(/^(ТОО|ИП|АО|ООО|LLP|LLC)\s+/i, '').split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
  }

  /** Ссылка на WhatsApp по номеру клиента. */
  whatsapp(phone: string): string {
    return 'https://wa.me/' + phone.replace(/\D/g, '');
  }

  // ─── Окно контрагента: контакты, реквизиты, сделки ───

  open(c: Counterparty): void {
    this.selected = c;
    this.requisites = null;
    this.requisitesError = false;
    this.lockScroll();
    if (c.userId == null) return;
    this.requisitesLoading = true;
    this.subscriptions.add(
      this.requisitesService.getRequisitesByUserId(c.userId).subscribe({
        next: r => { this.requisites = r; this.requisitesLoading = false; this.cdr.markForCheck(); },
        error: () => { this.requisitesError = true; this.requisitesLoading = false; this.cdr.markForCheck(); }
      })
    );
  }

  close(): void {
    this.selected = null;
    this.unlockScroll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selected) this.close();
  }

  private lockScroll(): void {
    lockPageScroll();
  }

  private unlockScroll(): void {
    unlockPageScroll();
  }
}
